import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import api from '@/api/api';
import { Calendar, Clock, Settings, Save, RefreshCw } from 'lucide-react';

const DateTimeSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    date_format: 'DD/MM/YYYY',
    time_format: '12-hour',
    week_start_day: 'Monday',
    working_days_per_month: 26,
    office_start_time: '09:00',
    office_end_time: '18:00',
    timezone: 'Asia/Kolkata',
    academic_year_start_month: 'July',
    first_half_end_time: '13:00',
    second_half_start_time: '14:00',
  });

  const dateFormats = [
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (14/01/2026)', example: '14/01/2026' },
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (01/14/2026)', example: '01/14/2026' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2026-01-14)', example: '2026-01-14' },
    { value: 'DD-Mon-YYYY', label: 'DD-Mon-YYYY (14-Jan-2026)', example: '14-Jan-2026' },
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY (14 Jan 2026)', example: '14 Jan 2026' },
  ];

  const timeFormats = [
    { value: '12-hour', label: '12-hour (02:30 PM)', example: '02:30 PM' },
    { value: '24-hour', label: '24-hour (14:30)', example: '14:30' },
  ];

  const weekDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const timezones = [
    { value: 'Asia/Kolkata', label: 'India Standard Time (IST) - UTC+5:30' },
    { value: 'Asia/Dhaka', label: 'Bangladesh Standard Time (BST) - UTC+6:00' },
    { value: 'Asia/Kathmandu', label: 'Nepal Time (NPT) - UTC+5:45' },
    { value: 'Asia/Colombo', label: 'Sri Lanka Time (SLST) - UTC+5:30' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/master-settings/date-time-settings');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Use defaults if no settings found
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post('/master-settings/date-time-settings', settings);
      toast.success('Date & Time settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      date_format: 'DD/MM/YYYY',
      time_format: '12-hour',
      week_start_day: 'Monday',
      working_days_per_month: 26,
      office_start_time: '09:00',
      office_end_time: '18:00',
      timezone: 'Asia/Kolkata',
      academic_year_start_month: 'July',
      first_half_end_time: '13:00',
      second_half_start_time: '14:00',
    });
    toast.info('Settings reset to defaults');
  };

  // Preview current date/time with selected format
  const getDatePreview = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const monthName = months[now.getMonth()].substring(0, 3);
    
    switch (settings.date_format) {
      case 'DD/MM/YYYY': return `${day}/${month}/${year}`;
      case 'MM/DD/YYYY': return `${month}/${day}/${year}`;
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      case 'DD-Mon-YYYY': return `${day}-${monthName}-${year}`;
      case 'DD MMM YYYY': return `${day} ${monthName} ${year}`;
      default: return `${day}/${month}/${year}`;
    }
  };

  const getTimePreview = () => {
    const now = new Date();
    const hours24 = now.getHours();
    const minutes = String(now.getMinutes()).padStart(2, '0');
    
    if (settings.time_format === '24-hour') {
      return `${String(hours24).padStart(2, '0')}:${minutes}`;
    } else {
      const hours12 = hours24 % 12 || 12;
      const ampm = hours24 >= 12 ? 'PM' : 'AM';
      return `${String(hours12).padStart(2, '0')}:${minutes} ${ampm}`;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="date-time-settings">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Settings className="w-7 h-7 text-blue-600" />
            Date & Time Settings
          </h1>
          <p className="text-gray-600 mt-1">Configure date format, time format, and working day settings for your institution</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetToDefaults} className="flex items-center gap-2">
            <RefreshCw size={16} />
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
            <Save size={16} />
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </div>

      {/* Live Preview */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-center gap-8">
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Current Date Preview</p>
              <p className="text-2xl font-bold text-blue-800">{getDatePreview()}</p>
            </div>
            <div className="h-12 w-px bg-blue-300"></div>
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-1">Current Time Preview</p>
              <p className="text-2xl font-bold text-indigo-800">{getTimePreview()}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Date Format Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-blue-600" />
              Date Format
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Date Format</label>
              <div className="space-y-2">
                {dateFormats.map((format) => (
                  <label 
                    key={format.value} 
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                      settings.date_format === format.value 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="date_format"
                      value={format.value}
                      checked={settings.date_format === format.value}
                      onChange={(e) => handleChange('date_format', e.target.value)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="ml-3 flex-1">
                      <span className="font-medium">{format.label}</span>
                    </span>
                    <span className="text-gray-500 text-sm bg-gray-100 px-2 py-1 rounded">
                      {format.example}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Time Format Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-indigo-600" />
              Time Format
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Time Format</label>
              <div className="space-y-2">
                {timeFormats.map((format) => (
                  <label 
                    key={format.value} 
                    className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                      settings.time_format === format.value 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="time_format"
                      value={format.value}
                      checked={settings.time_format === format.value}
                      onChange={(e) => handleChange('time_format', e.target.value)}
                      className="w-4 h-4 text-indigo-600"
                    />
                    <span className="ml-3 flex-1">
                      <span className="font-medium">{format.label}</span>
                    </span>
                    <span className="text-gray-500 text-sm bg-gray-100 px-2 py-1 rounded">
                      {format.example}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
              <select
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                {timezones.map((tz) => (
                  <option key={tz.value} value={tz.value}>{tz.label}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Working Days Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calendar className="w-5 h-5 text-green-600" />
              Working Days Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Week Start Day</label>
              <select
                value={settings.week_start_day}
                onChange={(e) => handleChange('week_start_day', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {weekDays.map((day) => (
                  <option key={day} value={day}>{day}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Working Days per Month
                <span className="text-xs text-gray-500 ml-2">(Used for salary pro-rata calculation)</span>
              </label>
              <input
                type="number"
                min="20"
                max="31"
                value={settings.working_days_per_month}
                onChange={(e) => handleChange('working_days_per_month', parseInt(e.target.value) || 26)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Standard: 26 days (excluding 4-5 Sundays)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Academic Year Start Month</label>
              <select
                value={settings.academic_year_start_month}
                onChange={(e) => handleChange('academic_year_start_month', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                {months.map((month) => (
                  <option key={month} value={month}>{month}</option>
                ))}
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Office Hours Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="w-5 h-5 text-orange-600" />
              Office Hours
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Office Start Time</label>
                <input
                  type="time"
                  value={settings.office_start_time}
                  onChange={(e) => handleChange('office_start_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Office End Time</label>
                <input
                  type="time"
                  value={settings.office_end_time}
                  onChange={(e) => handleChange('office_end_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">First Half End Time</label>
                <input
                  type="time"
                  value={settings.first_half_end_time}
                  onChange={(e) => handleChange('first_half_end_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">For half-day attendance</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Second Half Start Time</label>
                <input
                  type="time"
                  value={settings.second_half_start_time}
                  onChange={(e) => handleChange('second_half_start_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">After lunch break</p>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mt-4">
              <p className="text-sm text-orange-800">
                <strong>Note:</strong> Office hours are used for attendance auto-checkout feature. 
                If a staff member forgets to check out, the system will automatically check them out at the office end time.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Section */}
      <Card className="bg-gray-50">
        <CardContent className="p-4">
          <h3 className="font-semibold text-gray-800 mb-2">ℹ️ How these settings are used:</h3>
          <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
            <li><strong>Date Format:</strong> Applied to all date displays across the application</li>
            <li><strong>Time Format:</strong> Applied to attendance times, reports, and schedules</li>
            <li><strong>Working Days/Month:</strong> Used for salary pro-rata calculation in payroll</li>
            <li><strong>Office Hours:</strong> Used for attendance tracking and auto-checkout feature</li>
            <li><strong>Academic Year Start:</strong> Used for session management and reports</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default DateTimeSettings;
